import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { workspaceScenarios } from "@parkproitineraries/magicflow-core";
import {
  MagicFlowTripWorkspace,
  StrategyEditor,
  STRATEGY_EDITOR_DEFAULTS,
  useRecommendations,
} from "@parkproitineraries/magicflow-ui";
import { magicFlowClient } from "../../../services/magicFlowClient";
import toast from "../../../utils/toast";

const tabs = [
  { key: "overview", label: "Overview" },
  { key: "magicflow", label: "Lightning Lane" },
  { key: "strategy", label: "Strategy" },
];

/** Magic Kingdom fallback when /api/magicflow/attractions is unavailable. */
const MAGIC_KINGDOM_ATTRACTIONS = [
  { id: "att-space-mountain", name: "Space Mountain" },
  { id: "att-big-thunder", name: "Big Thunder Mountain Railroad" },
  { id: "att-pirates", name: "Pirates of the Caribbean" },
  { id: "att-jungle-cruise", name: "Jungle Cruise" },
  { id: "att-haunted-mansion", name: "Haunted Mansion" },
  { id: "att-tron", name: "TRON Lightcycle / Run" },
  { id: "att-seven-dwarfs", name: "Seven Dwarfs Mine Train" },
];

const DEFAULT_MF_PARK =
  import.meta.env.VITE_MAGICFLOW_PARK?.trim() || "Magic Kingdom";

function todayISODate() {
  return new Date().toISOString().slice(0, 10);
}

function strategyToEditorForm(s) {
  return {
    mode: s.mode,
    targetAttractions: s.targetAttractions ?? [],
    activeTimeWindow: s.activeTimeWindow ?? STRATEGY_EDITOR_DEFAULTS.activeTimeWindow,
    guardrails: s.guardrails ?? STRATEGY_EDITOR_DEFAULTS.guardrails,
  };
}

/** Shape expected by useRecommendations / coordinateRecommendation. */
function strategyToRuntime(s) {
  const createdAt =
    typeof s.createdAt === "string"
      ? s.createdAt
      : s.createdAt?.toISOString?.() ?? new Date().toISOString();
  const updatedAt =
    typeof s.updatedAt === "string"
      ? s.updatedAt
      : s.updatedAt?.toISOString?.() ?? createdAt;
  return {
    id: s.id,
    plannerAccountId: s.plannerAccountId,
    bookingGroupId: s.bookingGroupId,
    parkDate: s.parkDate,
    lightningLaneProduct: s.lightningLaneProduct,
    mode: s.mode,
    targetAttractions: s.targetAttractions,
    activeTimeWindow: s.activeTimeWindow,
    guardrails: s.guardrails,
    createdAt,
    updatedAt,
  };
}

const MAGICFLOW_LIVE_EVAL = false;

/**
 * CRM trip detail — /agent/crm/trips/:id.
 * MagicFlow: PARKPRO_UI_WIRING_SPEC Steps 7–9 (api-client + backend /api/magicflow).
 */
export default function TripDetail() {
  const { id: tripId } = useParams();
  const navigate = useNavigate();

  // MagicFlow is dev-only for now
  if (!import.meta.env.DEV) {
    navigate('/agent/crm/trips', { replace: true });
    return null;
  }
  const [activeTab, setActiveTab] = useState("magicflow");

  const [parkDate, setParkDate] = useState(todayISODate);
  const [mfPark, setMfPark] = useState(DEFAULT_MF_PARK);
  const [availableAttractions, setAvailableAttractions] = useState(MAGIC_KINGDOM_ATTRACTIONS);
  const [strategyEditorForm, setStrategyEditorForm] = useState(STRATEGY_EDITOR_DEFAULTS);
  const [loadedStrategy, setLoadedStrategy] = useState(null);
  const [runtimeStrategy, setRuntimeStrategy] = useState(
    () => workspaceScenarios.conservative.strategy,
  );
  const [strategyLoading, setStrategyLoading] = useState(true);
  const [strategySaving, setStrategySaving] = useState(false);

  const envPlanner = import.meta.env.VITE_MAGICFLOW_PLANNER_ACCOUNT_ID?.trim() || "";
  const envBooking = import.meta.env.VITE_MAGICFLOW_BOOKING_GROUP_ID?.trim() || "";

  const { entries: liveEntries, reset, stats } = useRecommendations(runtimeStrategy);

  const refreshMagicFlowData = useCallback(async () => {
    if (!tripId) return;
    setStrategyLoading(true);
    try {
      const [list, attractions] = await Promise.all([
        magicFlowClient.strategies.list(tripId),
        magicFlowClient.attractions.list(mfPark).catch(() => null),
      ]);

      const match = list.find((s) => s.parkDate === parkDate);
      if (match) {
        setLoadedStrategy(match);
        setStrategyEditorForm(strategyToEditorForm(match));
        setRuntimeStrategy(strategyToRuntime(match));
      } else {
        setLoadedStrategy(null);
        setStrategyEditorForm(STRATEGY_EDITOR_DEFAULTS);
        setRuntimeStrategy(workspaceScenarios.conservative.strategy);
      }

      if (attractions?.length) {
        const multi = attractions
          .filter((a) => a.llType === "multi_pass")
          .map((a) => ({ id: a.id, name: a.name }));
        if (multi.length > 0) {
          setAvailableAttractions(multi);
        }
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Could not load MagicFlow data (is the API running and the migration applied?)";
      toast.error(msg);
      setLoadedStrategy(null);
      setStrategyEditorForm(STRATEGY_EDITOR_DEFAULTS);
      setRuntimeStrategy(workspaceScenarios.conservative.strategy);
    } finally {
      setStrategyLoading(false);
    }
  }, [tripId, parkDate, mfPark]);

  useEffect(() => {
    refreshMagicFlowData();
  }, [refreshMagicFlowData]);

  useEffect(() => {
    reset();
  }, [tripId, parkDate, loadedStrategy?.id, reset]);

  const magicFlowEntries = MAGICFLOW_LIVE_EVAL
    ? liveEntries
    : workspaceScenarios.conservative.entries;

  const plannerAccountId = loadedStrategy?.plannerAccountId || envPlanner;
  const bookingGroupId = loadedStrategy?.bookingGroupId || envBooking;
  const canPersistStrategy = Boolean(plannerAccountId && bookingGroupId);

  const handleStrategySave = async (value) => {
    if (!tripId) return;
    if (!canPersistStrategy) {
      toast.error(
        "Add PlannerAccount and BookingGroup UUIDs (env VITE_MAGICFLOW_PLANNER_ACCOUNT_ID / VITE_MAGICFLOW_BOOKING_GROUP_ID, or load an existing strategy).",
      );
      return;
    }

    setStrategySaving(true);
    try {
      const input = {
        ...value,
        tripId,
        plannerAccountId,
        bookingGroupId,
        parkDate,
        park: loadedStrategy?.park || mfPark,
      };
      const saved = await magicFlowClient.strategies.upsert(input);
      setLoadedStrategy(saved);
      setStrategyEditorForm(strategyToEditorForm(saved));
      setRuntimeStrategy(strategyToRuntime(saved));
      reset();
      toast.success("Strategy saved");
      setActiveTab("magicflow");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to save strategy (check DB FKs for planner / booking group).";
      toast.error(msg);
    } finally {
      setStrategySaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={() => navigate("/agent/crm/trips")}
          className="text-sm text-gray-500 transition-colors hover:text-gray-700"
        >
          ← Back to trips
        </button>
        <h1 className="font-heading text-2xl font-semibold text-textPrimary">
          Trip Details
        </h1>
        <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
          {tripId}
        </span>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-6" aria-label="Trip detail tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "overview" && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-gray-500">
            Trip overview coming soon. This will show booking details, client info, and
            itinerary summary.
          </p>
        </div>
      )}

      {activeTab === "magicflow" && (
        <div className="max-w-4xl space-y-4">
          <MagicFlowTripWorkspace entries={magicFlowEntries} />
          {MAGICFLOW_LIVE_EVAL && (
            <p className="text-xs text-gray-500">
              Session: {stats.totalEntries} entries · {stats.recentSurfaceCount} surfaced (last hour) ·{" "}
              {stats.acceptedWindowCount} windows accepted
            </p>
          )}
        </div>
      )}

      {activeTab === "strategy" && (
        <div className="max-w-2xl space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">
              Park day & inventory
            </p>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-gray-600">Park date</span>
                <input
                  type="date"
                  value={parkDate}
                  onChange={(e) => setParkDate(e.target.value)}
                  className="rounded-md border border-gray-300 px-2 py-1.5 font-body text-sm"
                />
              </label>
              <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-sm">
                <span className="text-gray-600">Park (for attractions API)</span>
                <input
                  type="text"
                  value={mfPark}
                  onChange={(e) => setMfPark(e.target.value)}
                  className="rounded-md border border-gray-300 px-2 py-1.5 font-body text-sm"
                  placeholder="Magic Kingdom"
                />
              </label>
            </div>
            {!canPersistStrategy && (
              <p className="mt-3 text-xs text-amber-800">
                Saving requires valid <code className="rounded bg-amber-50 px-1">PlannerAccount</code> and{" "}
                <code className="rounded bg-amber-50 px-1">BookingGroup</code> UUIDs (see{" "}
                <code className="rounded bg-amber-50 px-1">VITE_MAGICFLOW_*</code> env vars or run DB
                migration and seed rows).
              </p>
            )}
            {strategyLoading && (
              <p className="mt-2 text-xs text-gray-500">Loading strategy…</p>
            )}
          </div>

          <StrategyEditor
            availableAttractions={availableAttractions}
            value={strategyEditorForm}
            onChange={setStrategyEditorForm}
            onSave={(v) => {
              void handleStrategySave(v);
            }}
            onCancel={() => setActiveTab("magicflow")}
            className="max-w-2xl"
          />
          {strategySaving && (
            <p className="text-xs text-gray-500">Saving…</p>
          )}
        </div>
      )}
    </div>
  );
}
